<template>
  <Teleport to="body">
    <div class="notify-wrapper">
      <TransitionGroup name="toast-slide">
        <div
          v-for="t in notifications"
          :key="t.id"
          class="notify-card"
          :class="`notify-${t.type || 'info'}`"
        >
          <!-- HEADER -->
          <div class="notify-header">
            <i :class="iconMap[t.type || 'info']"></i>
            <strong class="toast-title">
              {{ t.message || "Notification" }}
            </strong>

            <button class="close-btn" @click="remove(t.id)">
              <i class="bi bi-x"></i>
            </button>
          </div>

          <!-- BODY -->
          <div class="notify-body">
            <ul v-if="t.items?.length" class="notify-list">
              <li
                v-for="(item, i) in t.items"
                :key="i"
                class="notify-list-item"
                :class="{ clickable: typeof t.onItemClick === 'function' }"
                @click="handleItemClick(t, item)"
              >
                <div
                  class="container-notify-item"
                  :style="getItemBorderStyle(item)"
                >
                  <div
                    class="container-status-dot"
                    :style="getItemDotStyle(item)"
                  ></div>

                  <div class="container-notify-content">
                    <span class="container-label">Container No</span>

                    <span class="container-no-text" :style="getItemStyle(item)">
                      {{ getItemLabel(item) }}
                    </span>
                  </div>

                  <i
                    v-if="typeof t.onItemClick === 'function'"
                    class="bi bi-chevron-right container-arrow"
                  ></i>
                </div>
              </li>
            </ul>

            <p v-else>
              {{ t.message }}
            </p>
          </div>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<script setup>
import { computed } from "vue";
import { useToastStore } from "@/components/helper/toast";

const store = useToastStore();

const notifications = computed(() =>
  store.toasts.filter((t) => t.variant === "notification"),
);

const remove = store.remove;

const iconMap = {
  info: "bi bi-info-circle-fill",
  success: "bi bi-check-circle-fill",
  warning: "bi bi-exclamation-triangle-fill",
  danger: "bi bi-x-octagon-fill",
};

const getItemLabel = (item) => {
  if (typeof item === "string") return item;
  if (typeof item === "number") return String(item);

  if (item && typeof item === "object") {
    return item.label || item.container_no || item.text || "-";
  }

  return "-";
};

const isValidHexColor = (color) => {
  return (
    typeof color === "string" && /^#([0-9A-F]{3}|[0-9A-F]{6})$/i.test(color)
  );
};

const getItemBorderStyle = (item) => {
  if (!item || typeof item !== "object") {
    return {};
  }

  if (!isValidHexColor(item.closing_status)) {
    return {};
  }

  return {
    borderLeftColor: item.closing_status,
  };
};

const getItemDotStyle = (item) => {
  if (!item || typeof item !== "object") {
    return {};
  }

  if (!isValidHexColor(item.closing_status)) {
    return {};
  }

  return {
    backgroundColor: item.closing_status,
    boxShadow: `0 0 0 4px ${item.closing_status}22`,
  };
};
const getItemStyle = (item) => {
  if (!item || typeof item !== "object") {
    return {};
  }

  if (!isValidHexColor(item.closing_status)) {
    return {};
  }

  return {
    color: item.closing_status,
    borderColor: item.closing_status,
    backgroundColor: "#ffffff",
  };
};
const handleItemClick = (toast, item) => {
  if (typeof toast.onItemClick === "function") {
    toast.onItemClick(item);
  }
};
</script>

<style>
.notify-list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.notify-list-item {
  margin: 0;
}

.notify-list-item + .notify-list-item {
  margin-top: 8px;
}

.notify-list-item.clickable {
  cursor: pointer;
}

.container-notify-item {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 52px;
  padding: 10px 12px;
  border-radius: 10px;
  border-left: 5px solid rgba(255, 255, 255, 0.45);
  background: rgba(255, 255, 255, 0.16);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.14);
  transition:
    transform 0.18s ease,
    background 0.18s ease,
    box-shadow 0.18s ease;
}

.notify-list-item.clickable .container-notify-item:hover {
  transform: translateY(-1px);
  background: rgba(255, 255, 255, 0.24);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.18),
    0 8px 18px rgba(0, 0, 0, 0.12);
}

.container-status-dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  flex: 0 0 auto;
  background: #ffffff;
}

.container-notify-content {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
  flex: 1;
}

.container-label {
  font-size: 0.68rem;
  line-height: 1;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.78);
}

.container-no-text {
  width: fit-content;
  max-width: 100%;
  display: inline-block;
  padding: 3px 9px;
  border-radius: 999px;
  border: 1px solid transparent;
  font-size: 0.82rem;
  line-height: 1.2;
  font-weight: 800;
  background: #ffffff;
  color: #ffffff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.container-arrow {
  flex: 0 0 auto;
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.8);
}
/* ================== WRAPPER ================== */
.notify-wrapper {
  position: fixed;
  top: 1rem;
  right: 1rem;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

/* ================== CARD ================== */
.notify-card {
  width: 340px;
  border-radius: 12px;
  box-shadow: 0 14px 34px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  animation: fadeIn 0.25s ease;
  color: #ffffff;
}

/* ================== HEADER ================== */
.notify-header {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.75rem 1rem;
  font-size: 0.9rem;
}

.toast-title {
  font-weight: 700;
  flex: 1;
}

/* ================== BODY ================== */
.notify-body {
  padding: 0.75rem 1rem;
  font-size: 0.85rem;
  line-height: 1.5;
  background: rgba(0, 0, 0, 0.08);
  max-height: 220px;
  overflow-y: auto;
}

.notify-list {
  margin: 0;
  padding-left: 1rem;
}

/* ================== CLOSE ================== */
.close-btn {
  background: transparent;
  border: none;
  color: #ffffff;
  font-size: 1.1rem;
  cursor: pointer;
  opacity: 0.8;
}
.close-btn:hover {
  opacity: 1;
}

/* ================== ICON ================== */
.notify-header i {
  font-size: 1.1rem;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3));
}

/* ================== VARIANTS (HEX ONLY) ================== */
.notify-info {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
}

.notify-success {
  background: linear-gradient(135deg, #22c55e, #16a34a);
}

.notify-warning {
  background: linear-gradient(135deg, #f59e0b, #d97706);
}

.notify-danger {
  background: linear-gradient(135deg, #ef4444, #dc2626);
}

/* ================== TRANSITION ================== */
.toast-slide-enter-active,
.toast-slide-leave-active {
  transition: all 0.3s ease;
}
.toast-slide-enter-from {
  opacity: 0;
  transform: translateX(40px);
}
.toast-slide-leave-to {
  opacity: 0;
  transform: translateX(40px);
}

/* ================== ANIMATION ================== */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
